import React, { useEffect, useRef } from 'react';

const IDLE_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes
const SYNC_INTERVAL_MS = 3 * 60 * 1000; // 3 minutes

// Shell chrome (05-remediation.md §5.2) -- corre app-wide, `return null`, no renderiza nada:
// detecta actividad del usuario vía listeners de `window` y sincroniza los segundos activos al
// backend cada 3 min. Vive en `layout/` (lo monta `AppShell`), no en `features/*/components/`,
// porque sus `window.addEventListener` son legítimos acá (no es un componente presentacional).
// Recibe `userId` por prop de `AppShell` (que lo lee con `useCurrentUser()`).
const ActivityTracker = ({ socket, userId }) => {
    // Using refs so event listeners always access the latest values without re-binding
    const lastActivityTime = useRef(Date.now());
    const activeSeconds = useRef(0);
    const isIdle = useRef(false);

    useEffect(() => {
        if (!userId || !socket) return;

        // Reset variables when component mounts (or user changes)
        lastActivityTime.current = Date.now();
        activeSeconds.current = 0;
        isIdle.current = false;

        const handleUserActivity = () => {
            lastActivityTime.current = Date.now();
            if (isIdle.current) {
                isIdle.current = false;
                // console.log("User is back to active state");
            }
        };

        // Attach listeners
        window.addEventListener('mousemove', handleUserActivity);
        window.addEventListener('keydown', handleUserActivity);
        window.addEventListener('click', handleUserActivity);
        window.addEventListener('scroll', handleUserActivity);

        // Timer to increment active seconds every second
        const tickInterval = setInterval(() => {
            const now = Date.now();
            
            // Check if idle
            if (now - lastActivityTime.current > IDLE_TIMEOUT_MS) {
                if (!isIdle.current) {
                    isIdle.current = true;
                    // console.log("User went idle");
                }
            } else {
                // User is active, increment counter
                activeSeconds.current += 1;
            }
        }, 1000);

        const syncActivity = () => {
            if (activeSeconds.current > 0) {
                fetch('/api/users/activity/sync', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        userId,
                        seconds: activeSeconds.current
                    })
                }).catch(e => console.error("Error syncing activity", e));
                activeSeconds.current = 0;
            }
        };

        // Timer to sync data to backend
        const syncInterval = setInterval(syncActivity, SYNC_INTERVAL_MS);

        // Sync when leaving page
        const handleBeforeUnload = () => syncActivity();
        window.addEventListener('beforeunload', handleBeforeUnload);
        
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                syncActivity();
            }
        };
        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            window.removeEventListener('mousemove', handleUserActivity);
            window.removeEventListener('keydown', handleUserActivity);
            window.removeEventListener('click', handleUserActivity);
            window.removeEventListener('scroll', handleUserActivity);
            window.removeEventListener('beforeunload', handleBeforeUnload);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            clearInterval(tickInterval);
            clearInterval(syncInterval);
        };
    }, [userId, socket]);

    return null; // This is a logic-only component
};

export default ActivityTracker;
